import React, { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { clearAuthToken } from '@/services/apiClient';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  copied: boolean;
}

const STORAGE_KEY = '__last_error';

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    let restored: { message: string; stack?: string; componentStack?: string } | null = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) restored = JSON.parse(raw);
    } catch {
      // ignore
    }
    this.state = {
      hasError: false,
      error: null,
      componentStack: restored?.componentStack ?? null,
      copied: false,
    };
    // If we restored a prior error, surface it so the user can copy/share it.
    if (restored) {
      const err = new Error(restored.message);
      if (restored.stack) err.stack = restored.stack;
      this.state = {
        hasError: true,
        error: err,
        componentStack: restored.componentStack ?? null,
        copied: false,
      };
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Circuit breaker: a SecurityError from too many history.replaceState calls
    // means we hit a redirect loop (typically caused by a stale/expired auth
    // token bouncing between /login and a protected route in Safari). Clear
    // any auth state so the next reload starts clean.
    const msg = error?.message ?? '';
    const name = error?.name ?? '';
    if (
      (name === 'SecurityError' || /SecurityError/i.test(msg)) &&
      /replaceState/i.test(msg)
    ) {
      try {
        clearAuthToken();
        localStorage.removeItem('smplify_tenant_id');
      } catch {
        // ignore storage errors
      }
    }

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          message: error.message || 'Unknown error',
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        })
      );
    } catch {
      // sessionStorage may be unavailable (private mode, quota); ignore.
    }
    this.setState({ componentStack: errorInfo.componentStack });
  }

  private clearError = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    this.setState({ hasError: false, error: null, componentStack: null, copied: false });
  };

  private copyDetails = async () => {
    const { error, componentStack } = this.state;
    const payload = [
      `Message: ${error?.message ?? 'Unknown error'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `User-Agent: ${navigator.userAgent}`,
      '',
      'Stack:',
      error?.stack ?? '(no stack)',
      '',
      'Component stack:',
      componentStack ?? '(no component stack)',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Fallback: select via prompt
      window.prompt('Copy error details:', payload);
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, componentStack, copied } = this.state;
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full space-y-4">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
              <p className="text-muted-foreground">
                The application encountered an error. Copy the details below and share them so we can investigate.
              </p>
            </div>

            {error && (
              <div className="space-y-2">
                <pre className="text-left text-xs bg-muted p-4 rounded overflow-auto max-h-32 whitespace-pre-wrap">
                  <strong>{error.name || 'Error'}:</strong> {error.message || '(empty message)'}
                </pre>
                {error.stack && (
                  <pre className="text-left text-[11px] bg-muted/60 p-4 rounded overflow-auto max-h-48 whitespace-pre-wrap text-muted-foreground">
                    {error.stack}
                  </pre>
                )}
                {componentStack && (
                  <pre className="text-left text-[11px] bg-muted/60 p-4 rounded overflow-auto max-h-40 whitespace-pre-wrap text-muted-foreground">
                    Component stack:{componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={this.copyDetails} variant="default">
                {copied ? 'Copied!' : 'Copy error details'}
              </Button>
              <Button onClick={this.clearError} variant="secondary">
                Clear and continue
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  this.clearError();
                  window.location.reload();
                }}
              >
                Reload page
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  this.clearError();
                  window.location.href = '/login';
                }}
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
